import type { ComponentType } from "react";
import type { OnboardingStep } from "../types";
import { S1Welcome } from "./steps/S1Welcome";
import { S2PartnerPicker } from "./steps/S2PartnerPicker";
import { S2bNeither } from "./steps/S2bNeither";
import { S3Identity } from "./steps/S3Identity";
import { S3bVerifyEmail } from "./steps/S3bVerifyEmail";
import { S4aStatus } from "./steps/S4aStatus";
import { S4bLength } from "./steps/S4bLength";
import { S4cChildren } from "./steps/S4cChildren";
import { S5aFocus } from "./steps/S5aFocus";
import { S5bVision } from "./steps/S5bVision";
import { S7Preview } from "./steps/S7Preview";
import { S7bPrivacy } from "./steps/S7bPrivacy";
import { S8Consent } from "./steps/S8Consent";
import { S9HandoffStub } from "./steps/S9HandoffStub";
import { S11HomebaseWaiting } from "./steps/S11HomebaseWaiting";

export interface StepProps {
  onNext: () => void;
  onBack: () => void;
  onJump: (step: OnboardingStep) => void;
  currentStepIndex: number;
  totalSteps: number;
}

export interface StepConfig {
  key: OnboardingStep;
  component: ComponentType<StepProps>;
  showBack: boolean;
  /** If false, this step is reachable only via direct jump (e.g. error route). */
  inMainFlow: boolean;
}

/** Main happy-path flow, in order. Excludes dead-ends + post-completion screens. */
export const MAIN_FLOW_STEPS: StepConfig[] = [
  { key: "welcome", component: S1Welcome, showBack: false, inMainFlow: true },
  { key: "partner_picker", component: S2PartnerPicker, showBack: true, inMainFlow: true },
  // Contact (email + phone) now lives INSIDE the "A bit about you" step (S3Identity),
  // pre-filled from the partner's existing contact — there is no separate contact step.
  { key: "identity", component: S3Identity, showBack: true, inMainFlow: true },
  // Identity verification sits HERE, immediately after the step that captures the email,
  // and not at the end next to signing. Everything below this line used to be answered
  // with no identity check of any kind; the one-time code arrived at step 11 bundled with
  // the document and the signature. Splitting them puts the identity check where the
  // client just typed the address, and leaves the end of the flow for the part that
  // deserves attention — reading and signing the consent.
  //
  // The step is skippable BY CONSTRUCTION (see S3bVerifyEmail): it is an optimisation of
  // the common case and must never be able to stop onboarding. Do not add a completion
  // gate that depends on it.
  { key: "verify_email", component: S3bVerifyEmail, showBack: true, inMainFlow: true },
  { key: "relationship_status", component: S4aStatus, showBack: true, inMainFlow: true },
  { key: "relationship_length", component: S4bLength, showBack: true, inMainFlow: true },
  { key: "children", component: S4cChildren, showBack: true, inMainFlow: true },
  { key: "focus_areas", component: S5aFocus, showBack: true, inMainFlow: true },
  { key: "vision", component: S5bVision, showBack: true, inMainFlow: true },
  { key: "preview", component: S7Preview, showBack: true, inMainFlow: true },
  { key: "privacy", component: S7bPrivacy, showBack: true, inMainFlow: true },
  { key: "consent", component: S8Consent, showBack: true, inMainFlow: true },
  { key: "handoff", component: S9HandoffStub, showBack: true, inMainFlow: true },
  { key: "homebase", component: S11HomebaseWaiting, showBack: false, inMainFlow: true },
];

/** Off-main-flow steps reachable only by jump. */
export const SIDE_STEPS: StepConfig[] = [
  { key: "neither", component: S2bNeither, showBack: false, inMainFlow: false },
];

export const ALL_STEPS: StepConfig[] = [...MAIN_FLOW_STEPS, ...SIDE_STEPS];

export const STEP_BY_KEY: Record<OnboardingStep, StepConfig> = ALL_STEPS.reduce(
  (acc, s) => {
    acc[s.key] = s;
    return acc;
  },
  {} as Record<OnboardingStep, StepConfig>
);

/** Steps that count in the progress bar (skip welcome + homebase + sidesteps). */
export const PROGRESS_BAR_KEYS: OnboardingStep[] = [
  "partner_picker",
  "identity",
  "verify_email",
  "relationship_status",
  "relationship_length",
  "children",
  "focus_areas",
  "vision",
  "preview",
  "privacy",
  "consent",
  "handoff",
];

export function getProgressIndex(step: OnboardingStep): number {
  const idx = PROGRESS_BAR_KEYS.indexOf(step);
  return idx >= 0 ? idx : -1;
}
